function isValidCalendarDate(y: number, m: number, d: number): boolean {
    if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return false;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

export function parseDateOfBirthInputToIso(input: string): string | null {
    const t = input.trim();
    if (!t) return null;
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (isoMatch) {
        const y = Number(isoMatch[1]);
        const m = Number(isoMatch[2]);
        const d = Number(isoMatch[3]);
        if (!isValidCalendarDate(y, m, d)) return null;
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
    const parts = t.split(/[/\-.]/).map((p) => p.trim()).filter((p) => p.length > 0);
    if (parts.length !== 3) return null;
    let month: number;
    let day: number;
    let year: number;
    if (parts[0].length === 4) {
        year = Number(parts[0]);
        month = Number(parts[1]);
        day = Number(parts[2]);
    } else {
        month = Number(parts[0]);
        day = Number(parts[1]);
        year = Number(parts[2]);
        if (year < 100) year += 2000;
    }
    if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) return null;
    if (!isValidCalendarDate(year, month, day)) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Aligns with server @Past on LocalDate: calendar date must be strictly before today. */
export function isIsoDateStrictlyBeforeToday(iso: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dob = new Date(y, mo - 1, d);
    const t = new Date();
    const startToday = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    return dob < startToday;
}

/**
 * Progressively formats a US-style date input as the user types, inserting
 * `/` delimiters immediately after the month (2 digits) and day (2 digits).
 *
 * `previousValue` (the field's value before the keystroke) is used to detect
 * deletion so that backspace removes the auto-inserted `/` rather than the
 * formatter re-adding it after the user just deleted it.
 *
 * If the input already contains an ISO-style hyphen (e.g. `2024-11-15`), the
 * value is returned unchanged so ISO inputs continue to flow through to
 * `parseDateOfBirthInputToIso` on blur.
 */
export function autoFormatDateInput(value: string, previousValue: string = ""): string {
    if (value.includes("-")) return value;
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const isDeleting = value.length < previousValue.length;
    if (digits.length === 0) return "";
    if (digits.length < 2) return digits;
    if (digits.length === 2) return isDeleting ? digits : `${digits}/`;
    if (digits.length < 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length === 4) {
        const head = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
        return isDeleting ? head : `${head}/`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Formats an ISO timestamp as a long US date (e.g. May 15, 2026). Returns "--" when absent. */
export function formatLastLoginDate(iso: string | undefined | null): string {
    if (iso == null || iso === '') return '--';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Formats an ISO timestamp as US date and time (e.g. May 15, 2026, 2:30 PM). Returns "--" when absent. */
export function formatLastLoginDateTime(iso: string | undefined | null): string {
    if (iso == null || iso === '') return '--';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

export function formatDateOfBirthUs(iso: string | undefined | null): string | undefined {
    if (iso == null || iso === '' || iso === '-') return undefined;
    const trimmed = iso.trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        const d = Number(m[3]);
        return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return trimmed;
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
